import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { COPY } from './VigneronPage';

/**
 * VigneronModal — light-paper popup that shows Raffaello's full story from
 * inside the home page (opened by the "la mia storia" CTA in the bridge
 * section). Reuses the same `COPY` object exported by VigneronPage so the
 * translations stay in one place.
 */
export default function VigneronModal({ open, onClose, lang = 'it' }) {
  const overlayRef = useRef(null);
  const t = COPY[lang] || COPY.it;

  // Lock body scroll + pause Lenis + esc-to-close while the modal is up.
  // Lenis needs an explicit stop() otherwise its own rAF loop keeps scrolling
  // the underlying page even when body has overflow:hidden.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (window.__lenis) window.__lenis.stop();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      if (window.__lenis) window.__lenis.start();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className="vigneron-modal-overlay"
      onClick={onOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vigneron-modal-title"
      data-lenis-prevent
      data-testid="vigneron-modal"
    >
      <button
        type="button"
        className="vigneron-modal__close"
        onClick={onClose}
        aria-label={lang === 'it' ? 'Chiudi' : lang === 'fr' ? 'Fermer' : 'Close'}
        data-testid="vigneron-modal-close"
      >
        <X size={22} strokeWidth={1.5} />
      </button>
      <article
        className="vigneron-modal"
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent
      >
        <div className="vigneron-modal__body">
          <span className="eyebrow vigneron-modal__eyebrow">{t.eyebrow}</span>
          <h2 id="vigneron-modal-title" className="vigneron-modal__title">
            {t.h1a} {t.h1b}
          </h2>
          <p className="vigneron-modal__lead">{t.lead}</p>

          <section className="vigneron-modal__chapter">
            <h3>{t.ch1Title[0]} <em>{t.ch1Title[1]}</em></h3>
            {t.ch1.map((p, i) => <p key={i}>{p}</p>)}
            <blockquote>{t.quote1}</blockquote>
          </section>

          <section className="vigneron-modal__chapter">
            <h3>{t.ch2Title[0]} <em>{t.ch2Title[1]}</em></h3>
            {t.ch2.map((p, i) => <p key={i}>{p}</p>)}
          </section>

          <section className="vigneron-modal__chapter">
            <h3>{t.ch3Title[0]} <em>{t.ch3Title[1]}</em></h3>
            {t.ch3.map((p, i) => <p key={i}>{p}</p>)}
            <blockquote>{t.quote2}</blockquote>
          </section>
        </div>
      </article>
    </div>
  );
}
